<template>
    <div class="min-h-screen bg-slate-50 dark:bg-slate-900">
        <!-- Global meta tags available on every page -->
        <GlobalMetaTags
            :meta="$page.props.meta"
            :pageType="$page.props.pageType"
            :category="$page.props.category"
            :subcategory="$page.props.subcategory"
            :subsubcategory="$page.props.subsubcategory"
            :prompt="$page.props.prompt"
            :tag="$page.props.tag"
        />
        <!-- Header -->
        <header class="fixed w-full bg-white dark:bg-slate-800 border-b border-slate-900/10 dark:border-slate-50/[0.06] z-50">
            <!-- Primary Navigation Menu -->
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between xxxh-16">
                    <!-- Logo and Navigation Container -->
                    <div class="w-full flex flex-col">
                        <!-- Logo and Mobile Menu Button Row -->
                        <div class="grid grid-cols-2 sm:grid-cols-[1fr_max-content_1fr] items-center h-8 sm:h-12">
                            <!-- Logo and desktop navigation container -->
                            <div class="flex items-center justify-self-start col-start-1 row-start-1">
                                <!-- Logo -->
                                <Link :href="$page.props.urls.home" class="flex items-center">
                                    <img class="h-6 dark:hidden" :src="asset('/images/logo/logo.png')" alt="logo" loading="eager"/>
                                    <img class="hidden h-6 dark:block" :src="asset('/images/logo/logo-white.png')" alt="logo" loading="eager"/>
                                    <span class="ml-3 md:mr-3 text-xl xxxfont-semibold xxxxtext-slate-900 dark:text-white orbitron-400">{{ currentLocale === 'nl' ? 'Powerprompt' : 'GetPowerprompts' }}</span>
                                </Link>

                                <!-- Desktop Navigation Links - on same row as logo -->
                                <!--                                <div class="hidden sm:flex ml-10 space-x-8 orbitron">-->
                                <!--                                    <TopMenu v-if="$page?.props?.mainCategories" :mainCategories="$page.props.mainCategories"/>-->
                                <!--                                </div>-->
                            </div>

                            <!-- Centered slogan; add slight left margin on tablets (iPad mini) and reset on xl+ -->
                            <i class="hidden sm:block justify-self-center sm:col-start-2 text-center md:ml-2 xl:ml-0">{{ $t('slogan') }}</i>

                            <!-- Header Tools -->
                            <div class="flex items-center justify-self-end col-start-2 row-start-1 sm:col-start-3">
                                <!-- HeaderIcons - only visible on desktop -->
                                <div class="hidden sm:block">
                                    <HeaderIcons/>
                                </div>
                                <!-- Language Switcher - only visible on desktop -->
                                <div class="hidden sm:block">
                                    <LanguageSwitcher class="mr-2"/>
                                </div>

                                <!-- User Menu - only visible on desktop -->
                                <template v-if="$page.props.auth?.user">
                                    <div class="hidden sm:block ml-3 relative">
                                        <Dropdown align="right" width="48">
                                            <template #trigger>
                                                <span class="inline-flex rounded-md">
                                                    <button type="button"
                                                            class="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 hover:border-slate-300 focus:outline-none focus:text-slate-700 focus:border-slate-300 transition duration-150 ease-in-out cursor-pointer">
                                                        {{ $page.props.auth.user.name }}
                                                        <ChevronDownIcon class="ml-2 -mr-0.5 h-4 w-4"/>
                                                    </button>
                                                </span>
                                            </template>

                                            <template #content>
                                                <DropdownLink :href="String($page.props.urls.profile || '#')"
                                                              class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                    {{ $t('my_profile') }}
                                                </DropdownLink>
                                                <DropdownLink :href="String($page.props.urls.myRequests || '#')"
                                                              class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                    {{ $t('nav.my_requests') }}
                                                </DropdownLink>
                                                <DropdownLink :href="String($page.props.urls.myPrompts || '#')"
                                                              class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                    {{ $t('nav.my_prompts') }}
                                                </DropdownLink>
                                                <DropdownLink :href="String($page.props.urls.logout || '#')" method="post" as="button"
                                                              class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                    {{ $t('logout') }}
                                                </DropdownLink>
                                                <template v-if="isAdmin">
                                                    <a :href="String($page.props.urls.promptAanvragenIndex || '#')"
                                                       class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                        Prompt aanvragen
                                                    </a>
                                                    <a :href="String($page.props.urls.promptsIndex || '#')"
                                                       class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                        Toegevoegde prompts
                                                    </a>
                                                    <a :href="String($page.props.urls.analytics_php || '#')"
                                                       class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                        PHP analytics
                                                    </a>
                                                    <a :href="String($page.props.urls.analytics_umami_nl || '#')"
                                                       class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                        powerprompt.nl umami analytics
                                                    </a>
                                                    <a :href="String($page.props.urls.analytics_umami_non_nl || '#')"
                                                       class="block w-full px-4 py-2 text-sm leading-5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                                                        getpowerprompts.com umami analytics
                                                    </a>
                                                </template>
                                            </template>
                                        </Dropdown>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="flex items-center gap-x-2">
                                        <!-- Login link - only visible on desktop -->
                                        <Link :href="String($page.props.urls.login || '#')"
                                              method="get"
                                              class="hidden sm:inline-flex text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300">
                                            {{ $t('login') }}
                                        </Link>
                                    </div>
                                </template>

                                <!-- Mobile Menu Button -->
                                <div class="flex items-center sm:hidden ml-auto justify-end">
                                    <button @click="showingNavigationDropdown = !showingNavigationDropdown"
                                            class="inline-flex items-center justify-center pl-2 pr-0 py-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 focus:outline-none focus:bg-slate-100 focus:text-slate-500 transition duration-150 ease-in-out">
                                        <MenuIcon v-if="!showingNavigationDropdown" class="h-6 w-6"/>
                                        <XIcon v-else class="h-6 w-6"/>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- Mobile-only: Slogan 3 directly under the logo row -->
                        <div class="sm:hidden mt-0 mb-1 px-2" v-if="!isHome">
                            <p class="text-center text-[11px] leading-snug text-slate-600 dark:text-slate-300 whitespace-normal break-words">{{ $t('slogan3') }}</p>
                        </div>
                        <div class="pb-2 flex items-center sm:mt-0" :class="{ 'mt-2': isHome }">
                            <!-- Left slogan: content-sized on tablets so middle column can reach right edge; restore flex-1 on xl+ -->
                            <div class="hidden sm:block md:flex-none xl:flex-1 text-left whitespace-nowrap min-w-0">
                                <i class="hidden sm:block">{{ $t('slogan3') }}</i>
                            </div>
                            <!-- Middle: take remaining space on tablets so it can align flush right; restore 2x width on xl+ -->
                            <div class="flex-[2] md:flex-1 xl:flex-[2] flex items-center justify-center md:justify-end xl:justify-center min-w-0">
                                <PromptSearch @update:selected="handlePromptSelected" />
                            </div>
                            <!-- Right slogan: hide on tablet (incl. iPad Pro 1024px); show only on xl+ -->
                            <div class="hidden xl:flex text-right whitespace-nowrap">
                                <div class="flex flex-col items-end">
<!--                                    <i class="block">{{ $t('slogan2') }}</i>-->

                                    <div class="mt-2 flex items-center gap-2">
                                        <Link
                                            v-if="!$page.props.auth?.user"
                                            :href="String($page.props.urls.login || '#')"
                                            :data="{ intended: String(promptAanvragenCreateUrl || $page.props.urls.home) }"
                                            method="get"
                                            :class="ctaClass('prompt-aanvragen')"
                                        >
                                            {{ $t('cta.prompt_request') }}
                                        </Link>
                                        <Link
                                            v-else
                                            :href="String(promptAanvragenCreateUrl || '#')"
                                            :class="ctaClass('prompt-aanvragen')"
                                        >
                                            {{ $t('cta.prompt_request') }}
                                        </Link>

                                        <Link
                                            v-if="!$page.props.auth?.user"
                                            :href="String($page.props.urls.login || '#')"
                                            :data="{ intended: String(promptsCreateUrl || $page.props.urls.home) }"
                                            method="get"
                                            :class="ctaClass('prompts')"
                                        >
                                            {{ $t('cta.prompt_add') }}
                                        </Link>
                                        <Link
                                            v-else
                                            :href="String(promptsCreateUrl || '#')"
                                            :class="ctaClass('prompts')"
                                        >
                                            {{ $t('cta.prompt_add') }}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="hidden sm:block xl:hidden pb-2">
                            <div class="flex flex-col items-center text-center">
                                <i class="block text-sm text-slate-700 dark:text-slate-200">{{ $t('slogan2') }}</i>

                                <div class="mt-2 flex flex-wrap items-center justify-center gap-2">
                                    <Link
                                        v-if="!$page.props.auth?.user"
                                        :href="String($page.props.urls.login || '#')"
                                        :data="{ intended: String(promptAanvragenCreateUrl || $page.props.urls.home) }"
                                        method="get"
                                        :class="ctaClass('prompt-aanvragen')"
                                    >
                                        {{ $t('cta.prompt_request') }}
                                    </Link>
                                    <Link
                                        v-else
                                        :href="String(promptAanvragenCreateUrl || '#')"
                                        :class="ctaClass('prompt-aanvragen')"
                                    >
                                        {{ $t('cta.prompt_request') }}
                                    </Link>

                                    <Link
                                        v-if="!$page.props.auth?.user"
                                        :href="String($page.props.urls.login || '#')"
                                        :data="{ intended: String(promptsCreateUrl || $page.props.urls.home) }"
                                        method="get"
                                        :class="ctaClass('prompts')"
                                    >
                                        {{ $t('cta.prompt_add') }}
                                    </Link>
                                    <Link
                                        v-else
                                        :href="String(promptsCreateUrl || '#')"
                                        :class="ctaClass('prompts')"
                                    >
                                        {{ $t('cta.prompt_add') }}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mobile Navigation Menu -->
            <div :class="{'block': showingNavigationDropdown, 'hidden': !showingNavigationDropdown}" class="sm:hidden bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-lg">
                <div class="pt-2 pb-3 space-y-1">

                    <div class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <div class="flex flex-col gap-2">
                            <Link
                                v-if="!$page.props.auth?.user"
                                :href="String($page.props.urls.login || '#')"
                                :data="{ intended: String(promptAanvragenCreateUrl || $page.props.urls.home) }"
                                method="get"
                                class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md"
                            >
                                {{ $t('cta.prompt_request') }}
                            </Link>
                            <Link
                                v-else
                                :href="String(promptAanvragenCreateUrl || '#')"
                                class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md"
                            >
                                {{ $t('cta.prompt_request') }}
                            </Link>

                            <Link
                                v-if="!$page.props.auth?.user"
                                :href="String($page.props.urls.login || '#')"
                                :data="{ intended: String(promptsCreateUrl || $page.props.urls.home) }"
                                method="get"
                                class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md"
                            >
                                {{ $t('cta.prompt_add') }}
                            </Link>
                            <Link
                                v-else
                                :href="String(promptsCreateUrl || '#')"
                                class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md"
                            >
                                {{ $t('cta.prompt_add') }}
                            </Link>
                        </div>
                    </div>

                    <!-- Mobile user menu -->
                    <div v-if="$page.props.auth?.user" class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <div class="flex items-center px-3 py-2">
                            <span class="text-base font-medium text-slate-800 dark:text-white">
                                {{ $page.props.auth.user.name }}
                            </span>
                        </div>
                        <Link
                            :href="String($page.props.urls.profile || '#')"
                            class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2"
                        >
                            {{ $t('my_profile') }}
                        </Link>
                        <Link
                            :href="String($page.props.urls.logout || '#')"
                            method="post"
                            as="button"
                            class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md"
                        >
                            {{ $t('logout') }}
                        </Link>
                    </div>

                    <!-- Mobile-only tools for guests -->
                    <div v-if="!$page.props.auth?.user" class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <!-- Login link in mobile menu -->
                        <Link :href="String($page.props.urls.login || '#')"
                              :data="{ intended: $page.url }"
                              method="get"
                              class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2">
                            {{ $t('login') }}
                        </Link>

                        <!-- Register link in mobile menu -->
                        <Link :href="String($page.props.urls.register || '#')"
                              :data="{ intended: $page.url }"
                              method="get"
                              class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md">
                            {{ $t('register') }}
                        </Link>
                    </div>

                    <!-- Informational links (About, Privacy, Terms, Contact) in mobile menu -->
                    <div class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <Link :href="String($page.props.urls.about || '#')" class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2">{{ $t('pages.about.title') }}</Link>
                        <Link :href="String($page.props.urls.privacy || '#')" class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2">{{ $t('pages.privacy.title') }}</Link>
                        <Link :href="String($page.props.urls.terms || '#')" class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md mb-2">{{ $t('pages.terms.title') }}</Link>
                        <Link :href="String($page.props.urls.contact || '#')" class="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700 rounded-md">{{ $t('pages.contact.title') }}</Link>
                    </div>

                    <div class="px-2 py-3 border-t border-slate-200 dark:border-slate-700">
                        <div class="px-3">
                            <LanguageSwitcher/>
                        </div>
                        <div class="mt-3 px-3">
                            <HeaderIcons/>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- Main Content -->
        <main :class="mainClasses" xxxxstyle="background:green;">
            <!-- Page Header -->
            <div>
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-2 sm:mb-0">
                            <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">
                                {{ headerTitle }}
                            </h2>

                            <div
                                v-if="shouldShowHeaderShareIcon"
                                class="relative"
                                data-header-share-root
                            >
                                <button
                                    type="button"
                                    class="bg-slate-100 hover:bg-slate-200 text-slate-700 p-1.5 rounded-md transition-colors dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200"
                                    :aria-label="$t('share') || 'Deel deze pagina'"
                                    @click.stop="toggleHeaderShareMenu"
                                >
                                    <svg class="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle cx="18" cy="6" r="2.2" fill="currentColor" />
                                        <circle cx="6" cy="12" r="2.2" fill="currentColor" />
                                        <circle cx="18" cy="18" r="2.2" fill="currentColor" />
                                        <path
                                            d="M8 11.1l8-4.0M8 12.9l8 4.0"
                                            stroke="currentColor"
                                            stroke-width="1.8"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>

                                <div
                                    v-if="headerShareMenuOpen"
                                    class="absolute left-0 mt-2 z-30"
                                    data-header-share-root
                                    @click.stop
                                >
                                    <ShareDropdown
                                        :onWhatsApp="shareHeaderToWhatsApp"
                                        :onFacebook="shareHeaderToFacebook"
                                        :onTwitter="shareHeaderToTwitter"
                                        :onPinterest="shareHeaderToPinterest"
                                        :showPinterest="shouldShowHeaderPinterest"
                                    />
                                </div>
                            </div>
                        </div>
                        <nav v-if="$page.props.breadcrumbs" class="mt-2 mb-2" aria-label="Breadcrumb">
                            <ol class="flex items-center space-x-0 sm:space-x-2 xxxoverflow-x-auto">
                                <!-- Process each breadcrumb item -->
                                <template v-for="(item, index) in $page.props.breadcrumbs" :key="index">
                                    <!-- Skip the last item on prompt pages -->
                                    <template v-if="!(isPromptPage && index === $page.props.breadcrumbs.length - 1)">
                                        <!-- Item container with conditional display for mobile -->
                                        <li class="flex items-center flex-shrink-0"
                                            :class="{
                                                'hidden sm:flex': $page.props.breadcrumbs.length > 3 && index < 2,
                                                'flex-1': isLastVisibleItem(index) && $page.props.breadcrumbs.length > 1
                                            }">

                                            <!-- Add separator before items (except the first one) -->
                                            <template v-if="index > 0">
                                                <!-- For mobile with more than 3 items, if this is the first visible item (index=2), don't show the separator on mobile -->
                                                <template v-if="$page.props.breadcrumbs.length > 3 && index === 2">
                                                    <ChevronRightIcon class="hidden sm:block mx-2 h-5 w-5 text-slate-400"/>
                                                </template>
                                                <template v-else>
                                                    <ChevronRightIcon class="mx-1 sm:mx-2 h-4 sm:h-5 w-4 sm:w-5 text-slate-400"/>
                                                </template>
                                            </template>

                                            <!-- Item content -->
                                            <Link
                                                v-if="item.url || item.href"
                                                :href="item.url || item.href"
                                                class="text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                                            >
                                                <!-- On desktop, show full text -->
                                                <span class="hidden sm:inline">{{ $t(item.name) }}</span>

                                                <!-- On mobile: For the last visible item, truncate if too long; for others, just show without pipeline prefix -->
                                                <span
                                                    class="sm:hidden"
                                                    :class="{
                                                        'truncate block w-full': isLastVisibleItem(index),
                                                    }"
                                                >
                                                    {{ formatMobileCategoryName($t(item.name)) }}
                                                </span>
                                            </Link>
                                            <span
                                                v-else
                                                class="text-sm font-medium text-slate-700 dark:text-white"
                                            >
                                                <!-- On desktop, show full text -->
                                                <span class="hidden sm:inline">{{ $t(item.name) }}</span>

                                                <!-- On mobile: For the last visible item, truncate if too long; for others, just show without pipeline prefix -->
                                                <span
                                                    class="sm:hidden"
                                                    :class="{
                                                        'truncate block w-full': isLastVisibleItem(index),
                                                    }"
                                                >
                                                    {{ formatMobileCategoryName($t(item.name)) }}
                                                </span>
                                            </span>
                                        </li>
                                    </template>
                                </template>
                            </ol>
                        </nav>
                    </div>

                    <div class="hidden sm:flex items-start justify-center sm:justify-start mb-4 sm:ml-4">
                        <div v-if="iconComponent" class="flex items-center gap-3">
                            <div
                                class="h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center bg-blue-600 rounded-full p-1 sm:p-2"
                                :title="page.props.icon?.componentName"
                                data-tooltip-placement="top"
                            >
                                <component
                                    :is="iconComponent"
                                    class="h-10 w-10 sm:h-12 sm:w-12 text-white"
                                    :stroke-width="2.5"
                                />
                            </div>
                        </div>
                    </div>
                </div>
<!--                &lt;!&ndash; Mobile-only: only show slogan 2 under the title/breadcrumbs area &ndash;&gt;-->
<!--                <div class="sm:hidden mt-2 mb-4">-->
<!--                    <p class="text-center text-slate-500 dark:text-slate-400 text-xs leading-tight">{{ $t('slogan2') }}</p>-->
<!--                </div>-->
            </div>

            <!-- Page Content -->

            <slot></slot>
        </main>

        <!-- Footer -->
        <footer class="fixed bottom-0 w-full border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <div class="max-w-7xl mx-auto px-4 py-6">
                <div class="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
                    <p class="text-sm text-slate-500 dark:text-slate-400">
                        {{ $t('copyright_text') }}
                    </p>
                    <nav class="hidden sm:flex space-x-4 text-sm">
                        <Link :href="String($page.props.urls.about || '#')" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('pages.about.title') }}</Link>
                        <Link :href="String($page.props.urls.privacy || '#')" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('pages.privacy.title') }}</Link>
                        <Link :href="String($page.props.urls.terms || '#')" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('pages.terms.title') }}</Link>
                        <Link :href="String($page.props.urls.contact || '#')" class="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-500">{{ $t('pages.contact.title') }}</Link>
                    </nav>
                </div>
            </div>
        </footer>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue'
import {Link, usePage, router} from '@inertiajs/vue3'
import {asset} from '@/helpers'
import Dropdown from '@/Components/Dropdown.vue'
import DropdownLink from '@/Components/DropdownLink.vue'
import HeaderIcons from '@/Components/Icons/HeaderIcons.vue'
import TopMenu from '@/Components/Navigation/TopMenu.vue'
import {ChevronDownIcon, ChevronRightIcon, MenuIcon, XIcon} from '@/Components/Icons'
import {getIcon} from '@/Components/Icons/IconRegistry'
import LanguageSwitcher from '@/Components/Navigation/LanguageSwitcher.vue'
import PromptSearch from '@/Components/Prompts/PromptSearch.vue'
import {Clock} from 'lucide-vue-next';
import GlobalMetaTags from '@/Components/MetaTags/GlobalMetaTags.vue'
import ShareDropdown from '@/Components/Shared/ShareDropdown.vue'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-share.css'

 const props = defineProps({
     title: {
         type: String,
         default: null,
     },
 });

const showingNavigationDropdown = ref(false)

// Get the page object from Inertia
const page = usePage()

// Ref for the icon component
const iconComponent = ref(null)

// Function to format category names for mobile by removing the part before the pipeline
const formatMobileCategoryName = (name) => {
    if (name && name.includes(' | ')) {
        return name.split(' | ').pop();
    }
    return name;
}

// Function to determine if an item is the last visible breadcrumb on mobile
const isLastVisibleItem = (index) => {
    // If we're on a prompt page, the last item is hidden
    if (isPromptPage.value && index === page.props.breadcrumbs.length - 1) {
        return false;
    }

    // If we have more than 3 items and this is not the last visible one
    if (page.props.breadcrumbs.length > 3) {
        // For items that are hidden on mobile (first 2 when more than 3 items)
        if (index < 2) {
            return false;
        }

        // For the last visible item (either the last item or the second-to-last on prompt pages)
        return isPromptPage.value
            ? index === page.props.breadcrumbs.length - 2
            : index === page.props.breadcrumbs.length - 1;
    }

    // For 3 or fewer items, the last one is the last visible (or second-to-last on prompt pages)
    return isPromptPage.value
        ? index === page.props.breadcrumbs.length - 2
        : index === page.props.breadcrumbs.length - 1;
}

// Load the icon component when the component is mounted
onMounted(async () => {
    // Debug: Log all page props to see what's available
    // console.log('All page props:', page.props)
    if (page.props.icon && page.props.icon.componentName) {
        iconComponent.value = await getIcon(page.props.icon.componentName)
    }
})

// State for admin controls visibility
const isAdmin = computed(() => {
    const page = usePage();
    return page.props.auth &&
           page.props.auth.user &&
           page.props.auth.user.admin;
})

const isProdHost = computed(() => {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
        return window.location.hostname.endsWith('powerprompt.nl');
    }
    return false;
})

// Check if we're on a prompt page
const isPromptPage = computed(() => {
    return page.props.prompt?.id !== undefined;
})

const headerTitle = computed(() => {
    return props.title || page.props.title || '';
});

const selectedCategoryIdForCta = computed(() => {
    const fromSubsubcategory = page.props.subsubcategory?.id;
    if (fromSubsubcategory) {
        return fromSubsubcategory;
    }

    const fromPromptCategory = page.props.prompt?.category?.id;
    if (fromPromptCategory) {
        return fromPromptCategory;
    }

    return null;
});

const withCategoryIdParam = (url) => {
    if (!url) {
        return url;
    }

    const categoryId = selectedCategoryIdForCta.value;
    if (!categoryId) {
        return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}category_id=${categoryId}`;
};

const promptAanvragenCreateUrl = computed(() => {
    return withCategoryIdParam(String(page.props.urls.promptAanvragenCreate || ''));
});

const promptsCreateUrl = computed(() => {
    return withCategoryIdParam(String(page.props.urls.promptsCreate || ''));
});

const pathFromUrl = (url) => {
    if (!url) return '';
    return String(url).split('?')[0];
};

const isCtaActive = (key) => {
    const current = String(usePage().url || '').split('?')[0];
    if (key === 'prompt-aanvragen') {
        return current === pathFromUrl(promptAanvragenCreateUrl.value);
    }
    if (key === 'prompts') {
        return current === pathFromUrl(promptsCreateUrl.value);
    }
    return false;
};

const ctaClass = (key) => {
    const base = 'inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium border transition-colors';
    const active = ' bg-blue-600 border-blue-600 text-white hover:bg-blue-700';
    const inactive = ' bg-white border-blue-600 text-blue-700 hover:bg-blue-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800';
    return base + (isCtaActive(key) ? active : inactive);
};

// Check if we're on a tag overview page
const isTagPage = computed(() => {
    return !!page.props.tag;
})

const isImageSubcategoryPage = computed(() => {
    return !!(
        page.props.category &&
        page.props.category.name === 'Afbeeldingen' &&
        page.props.subcategory &&
        page.props.subcategory.id
    )
})

// --- Header share dropdown state for subsubcategory / image subcategory / tag pages ---
const headerShareMenuOpen = ref(false)

const toggleHeaderShareMenu = () => {
    headerShareMenuOpen.value = !headerShareMenuOpen.value
}

const closeHeaderShareMenu = () => {
    headerShareMenuOpen.value = false
}

const shouldShowHeaderShareIcon = computed(() => {
    if (isPromptPage.value) {
        return false
    }

    const hasSubsubcategoryShareContext = !!(page.props.subsubcategory && page.props.subsubcategory.id)

    return hasSubsubcategoryShareContext || isImageSubcategoryPage.value || isTagPage.value
})

// Canonical URL for current subsubcategory (without UTM), used for visible share text
const canonicalSubsubcategoryUrl = computed(() => {
    const sub = page.props.subsubcategory || null
    if (!sub) return ''

    let base = ''
    if (typeof window !== 'undefined' && window.location) {
        const origin = window.location.origin || ''
        if (sub.url && typeof sub.url === 'string') {
            // If the backend already provides an absolute URL, use it as-is; otherwise prefix with origin
            base = sub.url.startsWith('http') ? sub.url : origin + sub.url
        } else {
            base = window.location.href
        }
    } else if (sub.url && typeof sub.url === 'string') {
        base = sub.url
    }

    return base || ''
})

// UTM-decorated URL for analytics, used where the raw URL is less prominent (e.g. Twitter/Pinterest)
const subsubcategoryShareUrl = computed(() => {
    const base = canonicalSubsubcategoryUrl.value
    if (!base) return ''
    const separator = base.includes('?') ? '&' : '?'
    return `${base}${separator}utm_source=social&utm_medium=share&utm_campaign=image_gallery`
})

const getSubsubcategoryShareText = () => {
    const sub = page.props.subsubcategory || null
    let baseTitle = (sub && sub.name) || page.props.title || 'Pagina'

    // Ensure the visible name matches page titles like "Handleidingen Prompts"
    if (sub && sub.name && !sub.name.trim().toLowerCase().endsWith('prompts')) {
        baseTitle = `${sub.name} Prompts`
    }

    return `${baseTitle} - PowerPrompt.nl`
}

// Header share actions for subsubcategory
const shareSubsubcategoryToWhatsApp = () => {
    if (typeof window === 'undefined') return
    const text = getSubsubcategoryShareText()
    // Use UTM-decorated URL for analytics; fallback to canonical if needed
    const url = subsubcategoryShareUrl.value || canonicalSubsubcategoryUrl.value || window.location.href
    const fullText = `${text} ${url}`
    const waUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`
    window.open(waUrl, '_blank')
    closeHeaderShareMenu()
}

const shareSubsubcategoryToFacebook = () => {
    if (typeof window === 'undefined') return
    // Prefer UTM-decorated URL for Facebook as well
    const url = subsubcategoryShareUrl.value || canonicalSubsubcategoryUrl.value || window.location.href
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    window.open(fbUrl, '_blank')
    closeHeaderShareMenu()
}

const shareSubsubcategoryToTwitter = () => {
    if (typeof window === 'undefined') return
    const text = getSubsubcategoryShareText()
    const url = subsubcategoryShareUrl.value || window.location.href
    const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    window.open(twUrl, '_blank')
    closeHeaderShareMenu()
}

const pickBestImageUrl = (image) => {
    if (!image || typeof image !== 'object') {
        return ''
    }

    const candidates = [
        image.pinterest_url,
        image.social_url,
        image.social,
        image.card_avif_url,
        image.card_url,
        image.medium_url,
        image.url,
        image.original_url,
        image.large_url
    ]

    return candidates.find((src) => typeof src === 'string' && src.length > 0) || ''
}

const subsubcategoryPinterestImage = computed(() => {
    return pickBestImageUrl(page.props.subsubcategory?.representative_image || null)
})

const subcategoryPinterestImage = computed(() => {
    return pickBestImageUrl(page.props.subcategory?.representative_image || null)
})

const headerPinterestMedia = computed(() => {
    if (page.props.subsubcategory && subsubcategoryPinterestImage.value) {
        return subsubcategoryPinterestImage.value
    }

    if (isImageSubcategoryPage.value && subcategoryPinterestImage.value) {
        return subcategoryPinterestImage.value
    }

    return ''
})

const shouldShowHeaderPinterest = computed(() => {
    return !!headerPinterestMedia.value
})

const shareSubsubcategoryToPinterest = () => {
    if (typeof window === 'undefined') return
    const url = subsubcategoryShareUrl.value || window.location.href
    const desc = getSubsubcategoryShareText()
    const media = subsubcategoryPinterestImage.value
    const base = 'https://pinterest.com/pin/create/button/?'
    const params = new URLSearchParams()
    params.set('url', url)
    if (media) params.set('media', media)
    if (desc) params.set('description', desc)
    const pinUrl = base + params.toString()
    window.open(pinUrl, '_blank')
    closeHeaderShareMenu()
}

// --- Image subcategory (Afbeeldingen) share logic ---
const canonicalSubcategoryUrl = computed(() => {
    const subcat = page.props.subcategory || null
    if (!subcat) return ''

    let base = ''
    if (typeof window !== 'undefined' && window.location) {
        const origin = window.location.origin || ''
        if (subcat.url && typeof subcat.url === 'string') {
            base = subcat.url.startsWith('http') ? subcat.url : origin + subcat.url
        } else {
            base = window.location.href
        }
    } else if (subcat.url && typeof subcat.url === 'string') {
        base = subcat.url
    }

    return base || ''
})

const subcategoryShareUrl = computed(() => {
    const base = canonicalSubcategoryUrl.value
    if (!base) return ''
    const separator = base.includes('?') ? '&' : '?'
    return `${base}${separator}utm_source=social&utm_medium=share&utm_campaign=image_gallery`
})

const getSubcategoryShareText = () => {
    const subcat = page.props.subcategory || null
    const name = (subcat && subcat.name) || page.props.title || 'Pagina'
    return `${name} Prompts - PowerPrompt.nl`
}

const shareSubcategoryToWhatsApp = () => {
    if (typeof window === 'undefined') return
    const text = getSubcategoryShareText()
    const url = subcategoryShareUrl.value || canonicalSubcategoryUrl.value || window.location.href
    const fullText = `${text} ${url}`
    const waUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`
    window.open(waUrl, '_blank')
    closeHeaderShareMenu()
}

const shareSubcategoryToFacebook = () => {
    if (typeof window === 'undefined') return
    const url = subcategoryShareUrl.value || canonicalSubcategoryUrl.value || window.location.href
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    window.open(fbUrl, '_blank')
    closeHeaderShareMenu()
}

const shareSubcategoryToTwitter = () => {
    if (typeof window === 'undefined') return
    const text = getSubcategoryShareText()
    const url = subcategoryShareUrl.value || window.location.href
    const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    window.open(twUrl, '_blank')
    closeHeaderShareMenu()
}

const shareSubcategoryToPinterest = () => {
    if (typeof window === 'undefined') return
    const url = subcategoryShareUrl.value || window.location.href
    const desc = getSubcategoryShareText()
    const media = subcategoryPinterestImage.value
    const base = 'https://pinterest.com/pin/create/button/?'
    const params = new URLSearchParams()
    params.set('url', url)
    if (media) params.set('media', media)
    if (desc) params.set('description', desc)
    const pinUrl = base + params.toString()
    window.open(pinUrl, '_blank')
    closeHeaderShareMenu()
}

// --- Tag page share logic ---
const canonicalTagUrl = computed(() => {
    if (!isTagPage.value) {
        return ''
    }

    if (page.props.meta) {
        if (typeof page.props.meta.canonical === 'string' && page.props.meta.canonical.length > 0) {
            return page.props.meta.canonical
        }
        if (typeof page.props.meta.url === 'string' && page.props.meta.url.length > 0) {
            return page.props.meta.url
        }
    }

    if (typeof window !== 'undefined' && window.location) {
        return window.location.href
    }

    return ''
})

const tagShareUrl = computed(() => {
    const base = canonicalTagUrl.value
    if (!base) return ''
    const separator = base.includes('?') ? '&' : '?'
    return `${base}${separator}utm_source=social&utm_medium=share&utm_campaign=tag_page`
})

const getTagShareText = () => {
    const tagTitle = page.props.title || (page.props.tag ? `Tag ${page.props.tag}` : 'PowerPrompt')
    return `${tagTitle} - PowerPrompt.nl`
}

const shareTagToWhatsApp = () => {
    if (!isTagPage.value || typeof window === 'undefined') return
    const url = tagShareUrl.value || canonicalTagUrl.value || window.location.href
    const fullText = `${getTagShareText()} ${url}`.trim()
    const waUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`
    window.open(waUrl, '_blank')
    closeHeaderShareMenu()
}

const shareTagToFacebook = () => {
    if (!isTagPage.value || typeof window === 'undefined') return
    const url = tagShareUrl.value || canonicalTagUrl.value || window.location.href
    if (!url) return
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    window.open(fbUrl, '_blank')
    closeHeaderShareMenu()
}

const shareTagToTwitter = () => {
    if (!isTagPage.value || typeof window === 'undefined') return
    const text = getTagShareText()
    const url = tagShareUrl.value || canonicalTagUrl.value || ''
    const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}${url ? `&url=${encodeURIComponent(url)}` : ''}`
    window.open(twUrl, '_blank')
    closeHeaderShareMenu()
}

const shareTagToPinterest = () => {
    if (!isTagPage.value || typeof window === 'undefined') return
    const url = tagShareUrl.value || canonicalTagUrl.value || ''
    if (!url) return
    const desc = getTagShareText()
    const base = 'https://pinterest.com/pin/create/button/?'
    const params = new URLSearchParams()
    params.set('url', url)
    if (desc) params.set('description', desc)
    const pinUrl = base + params.toString()
    window.open(pinUrl, '_blank')
    closeHeaderShareMenu()
}

const shareHeaderToWhatsApp = () => {
    if (isTagPage.value) {
        shareTagToWhatsApp()
    } else if (page.props.subsubcategory && page.props.subsubcategory.id) {
        shareSubsubcategoryToWhatsApp()
    } else if (page.props.category && page.props.category.name === 'Afbeeldingen' && page.props.subcategory && page.props.subcategory.id) {
        shareSubcategoryToWhatsApp()
    } else {
        // Fallback to current URL via Twitter intent
        shareSubcategoryToWhatsApp()
    }
}

const shareHeaderToFacebook = () => {
    if (isTagPage.value) {
        shareTagToFacebook()
    } else if (page.props.subsubcategory && page.props.subsubcategory.id) {
        shareSubsubcategoryToFacebook()
    } else if (page.props.category && page.props.category.name === 'Afbeeldingen' && page.props.subcategory && page.props.subcategory.id) {
        shareSubcategoryToFacebook()
    } else {
        shareSubcategoryToFacebook()
    }
}

const shareHeaderToTwitter = () => {
    if (isTagPage.value) {
        shareTagToTwitter()
    } else if (page.props.subsubcategory && page.props.subsubcategory.id) {
        shareSubsubcategoryToTwitter()
    } else if (page.props.category && page.props.category.name === 'Afbeeldingen' && page.props.subcategory && page.props.subcategory.id) {
        shareSubcategoryToTwitter()
    } else {
        shareSubcategoryToTwitter()
    }
}

const shareHeaderToPinterest = () => {
    if (isTagPage.value) {
        shareTagToPinterest()
    } else if (page.props.subsubcategory && page.props.subsubcategory.id) {
        shareSubsubcategoryToPinterest()
    } else if (page.props.category && page.props.category.name === 'Afbeeldingen' && page.props.subcategory && page.props.subcategory.id) {
        shareSubcategoryToPinterest()
    } else {
        shareSubcategoryToPinterest()
    }
}

// Simple share handler for the prompt header share icon
const headerSharePrompt = () => {
    if (!isPromptPage.value) return;

    const title = page.props.title || page.props.prompt?.title || 'Prompt';
    let url = '';

    if (typeof window !== 'undefined' && window.location) {
        url = window.location.href;
    } else if (page.props.prompt?.url) {
        url = page.props.prompt.url;
    }

    const shareText = `${title} - PowerPrompt.nl`;

    // Use Web Share API when available
    if (typeof navigator !== 'undefined' && navigator.share) {
        navigator.share({
            title: shareText,
            url
        }).catch(() => {
            // silently ignore if user cancels
        });
        return;
    }

    // Fallback: Twitter share
    if (typeof window !== 'undefined') {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
        window.open(twitterUrl, '_blank');
    }
}

// Function to get the appropriate URL for recalculating icons based on the current page
const getRecalculateIconUrl = () => {
    // For category pages
    if (page.props.category?.id) {
        return `/categories/${page.props.category.id}/recalculate-icon`;
    }
    // For subcategory pages
    else if (page.props.subcategory?.id) {
        return `/categories/${page.props.subcategory.id}/recalculate-icon`;
    }
    // For subsubcategory pages
    else if (page.props.subsubcategory?.id) {
        return `/categories/${page.props.subsubcategory.id}/recalculate-icon`;
    }
    // For prompt pages
    else if (page.props.prompt?.id) {
        // For prompt pages, we need to use the subsubcategory ID instead of the prompt ID
        if (page.props.subsubcategory?.id) {
            return `/categories/${page.props.subsubcategory.id}/recalculate-icon`;
        }
        // Fallback to subcategory if available
        else if (page.props.subcategory?.id) {
            return `/categories/${page.props.subcategory.id}/recalculate-icon`;
        }
        // Fallback to category if available
        else if (page.props.category?.id) {
            return `/categories/${page.props.category.id}/recalculate-icon`;
        }
    }
    // Default fallback
    return '/admin/recalculate-icons';
}

// No form submission needed for simple link approach

// Computed property for current path
const currentPath = computed(() => {
    return usePage().url.split('?')[0]; // Get the path without query parameters
})

// Check if recalculate parameter is active in the URL
const isRecalculateActive = computed(() => {
    return usePage().url.includes('recalculate=true');
})

// Function to handle prompt selection from the search component
const handlePromptSelected = (prompt) => {
    if (!prompt) return;

    // If the prompt has a URL, use Inertia to navigate to it without a full page reload
    if (prompt.url) {
        router.visit(prompt.url, {
            preserveScroll: false,  // Don't preserve scroll position
            preserveState: false,   // Don't preserve component state
        });
    } else if (prompt.id) {
        console.log('Selected prompt without URL, ID:', prompt.id);
    }
};

const currentLocale = computed(() => {
    return usePage().props.locale || this.$i18n.locale;
});

// Determine if current page is the Home page (with or without locale prefix)
const isHome = computed(() => {
    const path = currentPath.value || '/';
    if (path === '/' || path === '') return true;
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 1) {
        const loc = segments[0].toLowerCase();
        return ['nl', 'en', 'de', 'fr', 'pt', 'es', 'zh'].includes(loc);
    }
    return false;
})

// Main container classes: full width for pages that set $page.props.fullWidth
const mainClasses = computed(() => {
    const full = !!usePage().props.fullWidth
    const base = 'pt-28 pb-20 sm:pb-32'
    return full
        ? `w-full px-0 ${base}`
        : `max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 ${base}`
})
</script>

<style scoped>
.grid {
	gap: 1.5rem; /* This is equivalent to gap-6 */
}

/* Hide Google reCAPTCHA badge on small screens */
@media (max-width: 640px) {
	.grecaptcha-badge {
		display: none !important;
	}
}
</style>
